import { useCallback, useEffect, useRef, useState } from 'react';
import { practiceWord } from '../api/client';
import type { PracticeResult, PracticeStatus, PracticeDifficulty } from '../api/client';

export interface Attempt {
  matchPct: number;
  verdict: 'perfect' | 'close' | 'needs_work';
  feedback: string;
  audioUrl: string;
  status?: PracticeStatus;
  heard?: string;
}

interface UseWordPracticeOpts {
  surah: number;
  ayah: number;
  word: string;
  repairHint?: string;
  language?: string;
  difficulty?: PracticeDifficulty;
}

export function useWordPractice({
  surah, ayah, word, repairHint = '', language = 'en', difficulty = 'normal',
}: UseWordPracticeOpts) {
  const [recording, setRecording] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [inputLevel, setInputLevel] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number>(0);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const attemptsRef = useRef<Attempt[]>([]);

  useEffect(() => { attemptsRef.current = attempts; }, [attempts]);

  const stopRecording = useCallback(() => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setInputLevel(0);
    setRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    // Cancel any in-flight prefetches
    window.dispatchEvent(new CustomEvent('tajweed:abort-prefetches'));
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 44100 },
      });
      streamRef.current = stream;

      const ctx = new AudioContext();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(data);
        let sumSq = 0;
        for (let i = 0; i < data.length; i++) {
          const s = (data[i] - 128) / 128;
          sumSq += s * s;
        }
        const rms = Math.sqrt(sumSq / data.length);
        setInputLevel(Math.min(rms * 3, 1));
        animFrameRef.current = requestAnimationFrame(tick);
      };
      animFrameRef.current = requestAnimationFrame(tick);

      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setEvaluating(true);
        const attemptNumber = attemptsRef.current.length + 1;
        const lastFeedback = attemptsRef.current[attemptsRef.current.length - 1]?.feedback || '';
        try {
          const result: PracticeResult = await practiceWord(
            blob, surah, ayah, word, repairHint, language, attemptNumber, lastFeedback, difficulty
          );
          setAttempts((prev) => [
            ...prev,
            {
              matchPct: result.matchPct,
              verdict: result.verdict,
              feedback: result.feedback,
              audioUrl: url,
              status: result.status,
              heard: result.heard,
            },
          ]);
          // Auto-play teacher feedback
          if (result.feedbackAudioB64) {
            try {
              const bin = atob(result.feedbackAudioB64);
              const bytes = new Uint8Array(bin.length);
              for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
              const fbBlob = new Blob([bytes], { type: 'audio/mpeg' });
              const fbUrl = URL.createObjectURL(fbBlob);
              new Audio(fbUrl).play().catch(() => {});
            } catch {}
          }
        } catch {
          setAttempts((prev) => [
            ...prev,
            { matchPct: 0, verdict: 'needs_work', feedback: 'Evaluation failed. Try again.', audioUrl: url },
          ]);
        } finally {
          setEvaluating(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start(250);
      setRecording(true);
    } catch (err) {
      console.error('Mic access denied', err);
    }
  }, [surah, ayah, word, repairHint, language, difficulty]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    audioContextRef.current?.close();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const reset = useCallback(() => {
    setAttempts([]);
    setEvaluating(false);
    if (recording) stopRecording();
  }, [recording, stopRecording]);

  return {
    recording, evaluating, attempts, inputLevel,
    startRecording, stopRecording, reset,
  };
}
